import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  HiOutlineRefresh, HiOutlineDownload, HiOutlineChevronDown,
  HiOutlineChevronRight, HiOutlineServer, HiOutlineCloud,
  HiOutlineCheckCircle, HiOutlineXCircle, HiOutlineClock,
} from 'react-icons/hi';
import { getGpuInfo, downloadGpuInfoExcel } from '../services/api';
import './GpuInfo.css';

function statusBadge(status) {
  if (!status) return <span className="status-badge neutral">Unknown</span>;
  const upper = status.toUpperCase();
  if (upper === 'RUNNING' || upper === 'READY' || upper === 'SUCCEEDED' || upper === 'COMPLETED')
    return <span className="status-badge success"><HiOutlineCheckCircle size={12} /> {status}</span>;
  if (upper === 'TERMINATED' || upper === 'STOPPED' || upper === 'FAILED')
    return <span className="status-badge error"><HiOutlineXCircle size={12} /> {status}</span>;
  return <span className="status-badge neutral">{status}</span>;
}

function formatGpuType(raw) {
  if (!raw) return '—';
  return raw
    .replace(/-/g, ' ')
    .replace(/\bnvidia\b/i, 'NVIDIA')
    .replace(/\btesla\b/i, 'Tesla')
    .replace(/\b([a-z])(\d+\w*)/gi, (_, letter, rest) => letter.toUpperCase() + rest.toUpperCase())
    .replace(/\bgpu\b/i, 'GPU');
}

function ProjectCard({ project, defaultExpanded }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { projectId, projectName, quotas = [], vms = [], cloudRuns = [], cloudRunJobs = [], errors = [] } = project;
  const label = projectName || projectId;

  const totalVmGpus = vms.reduce((s, vm) =>
    s + (vm.accelerators || []).reduce((a, acc) => a + acc.count, 0), 0);
  const totalCrGpus = cloudRuns.reduce((s, cr) => s + (cr.gpuCount || 0), 0);
  const totalJobGpus = cloudRunJobs.reduce((s, j) => s + (j.gpuCount || 0), 0);

  return (
    <div className="gpu-project-card">
      <div className="gpu-project-header" onClick={() => setExpanded(e => !e)}>
        {expanded ? <HiOutlineChevronDown size={16} /> : <HiOutlineChevronRight size={16} />}
        <h2 className="gpu-project-title">{label}</h2>
        <span className="gpu-project-id">{projectId}</span>
        <div className="gpu-project-badges">
          {quotas.length > 0 && <span className="gpu-badge quota">{quotas.length} Quota{quotas.length > 1 ? 's' : ''}</span>}
          {vms.length > 0 && <span className="gpu-badge vm">{vms.length} VM{vms.length > 1 ? 's' : ''}</span>}
          {cloudRuns.length > 0 && <span className="gpu-badge cr">{cloudRuns.length} Service{cloudRuns.length > 1 ? 's' : ''}</span>}
          {cloudRunJobs.length > 0 && <span className="gpu-badge job">{cloudRunJobs.length} Job{cloudRunJobs.length > 1 ? 's' : ''}</span>}
        </div>
      </div>

      {expanded && (
        <div className="gpu-project-body">
          {/* Quotas */}
          {quotas.length > 0 && (
            <div className="gpu-sub-section">
              <h3 className="gpu-sub-title">GPU Quotas</h3>
              <div className="gpu-quota-grid">
                {quotas.map((q, i) => (
                  <div key={i} className="gpu-quota-item">
                    <div className="gpu-quota-name">{q.metric}</div>
                    <div className="gpu-quota-bar-wrap">
                      <div className="gpu-quota-bar">
                        <div
                          className={`gpu-quota-fill ${q.usagePercent >= 80 ? 'high' : q.usagePercent >= 50 ? 'mid' : 'low'}`}
                          style={{ width: `${Math.min(q.usagePercent, 100)}%` }}
                        />
                      </div>
                      <span className="gpu-quota-pct">{q.usagePercent}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* VMs */}
          {vms.length > 0 && (
            <div className="gpu-sub-section">
              <h3 className="gpu-sub-title"><HiOutlineServer size={14} /> GPU VMs <span className="gpu-sub-count">{totalVmGpus} GPU{totalVmGpus > 1 ? 's' : ''}</span></h3>
              <div className="resource-table-wrap">
                <div className="resource-table-scroll">
                  <table className="resource-table gpu-compact-table">
                    <thead>
                      <tr>
                        <th>VM Name</th>
                        <th>Zone</th>
                        <th>Status</th>
                        <th>Machine Type</th>
                        <th>GPU Type</th>
                        <th>GPU Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vms.flatMap(vm =>
                        (vm.accelerators || []).map((acc, i) => (
                          <tr key={`${vm.name}-${i}`}>
                            <td className="mono">{vm.name}</td>
                            <td>{vm.zone}</td>
                            <td>{statusBadge(vm.status)}</td>
                            <td className="mono">{vm.machineType}</td>
                            <td className="mono">{formatGpuType(acc.type)}</td>
                            <td className="gpu-count-cell">{acc.count}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Cloud Runs */}
          {cloudRuns.length > 0 && (
            <div className="gpu-sub-section">
              <h3 className="gpu-sub-title"><HiOutlineCloud size={14} /> GPU Cloud Run Services <span className="gpu-sub-count">{totalCrGpus} GPU{totalCrGpus > 1 ? 's' : ''}</span></h3>
              <div className="resource-table-wrap">
                <div className="resource-table-scroll">
                  <table className="resource-table gpu-compact-table">
                    <thead>
                      <tr>
                        <th>Service Name</th>
                        <th>Region</th>
                        <th>Status</th>
                        <th>GPU Type</th>
                        <th>GPU Count</th>
                        <th>CPU</th>
                        <th>Memory</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cloudRuns.map((svc, i) => (
                        <tr key={`${svc.name}-${i}`}>
                          <td className="mono">{svc.name}</td>
                          <td>{svc.region}</td>
                          <td>{statusBadge(svc.status)}</td>
                          <td className="mono">{formatGpuType(svc.gpuType)}</td>
                          <td className="gpu-count-cell">{svc.gpuCount}</td>
                          <td className="mono">{svc.cpu}</td>
                          <td className="mono">{svc.memory}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Cloud Run Jobs */}
          {cloudRunJobs.length > 0 && (
            <div className="gpu-sub-section">
              <h3 className="gpu-sub-title"><HiOutlineClock size={14} /> GPU Cloud Run Jobs <span className="gpu-sub-count">{totalJobGpus} GPU{totalJobGpus > 1 ? 's' : ''}</span></h3>
              <div className="resource-table-wrap">
                <div className="resource-table-scroll">
                  <table className="resource-table gpu-compact-table">
                    <thead>
                      <tr>
                        <th>Job Name</th>
                        <th>Region</th>
                        <th>Last Execution</th>
                        <th>GPU Type</th>
                        <th>GPU Count</th>
                        <th>CPU</th>
                        <th>Memory</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cloudRunJobs.map((job, i) => (
                        <tr key={`${job.name}-${i}`}>
                          <td className="mono">{job.name}</td>
                          <td>{job.region}</td>
                          <td>{statusBadge(job.lastExecStatus)}</td>
                          <td className="mono">{formatGpuType(job.gpuType)}</td>
                          <td className="gpu-count-cell">{job.gpuCount}</td>
                          <td className="mono">{job.cpu}</td>
                          <td className="mono">{job.memory}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {quotas.length === 0 && vms.length === 0 && cloudRuns.length === 0 && cloudRunJobs.length === 0 && errors.length === 0 && (
            <p className="gpu-empty">No GPU resources found for this project.</p>
          )}

          {errors.length > 0 && (
            <div className="gpu-project-errors">
              {errors.map((e, i) => <span key={i} className="gpu-error-msg">{e}</span>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function GpuInfo() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getGpuInfo();
      setData(res.data || []);
    } catch (err) {
      toast.error('Failed to load GPU info: ' + (err.response?.data?.error || err.message));
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalGpuVMs = data.reduce((sum, p) => sum + (p.vms?.length || 0), 0);
  const totalGpuCR = data.reduce((sum, p) => sum + (p.cloudRuns?.length || 0), 0);
  const totalGpuJobs = data.reduce((sum, p) => sum + (p.cloudRunJobs?.length || 0), 0);
  const totalGpuCount = data.reduce((sum, p) =>
    sum + (p.vms || []).reduce((vs, vm) =>
      vs + (vm.accelerators || []).reduce((as, a) => as + a.count, 0), 0)
    + (p.cloudRuns || []).reduce((cs, cr) => cs + (cr.gpuCount || 0), 0)
    + (p.cloudRunJobs || []).reduce((js, j) => js + (j.gpuCount || 0), 0), 0);
  const totalQuotaUsed = data.reduce((sum, p) =>
    sum + (p.quotas || []).reduce((qs, q) => qs + q.usage, 0), 0);
  const totalQuotaLimit = data.reduce((sum, p) =>
    sum + (p.quotas || []).reduce((qs, q) => qs + q.limit, 0), 0);
  const overallQuotaPct = totalQuotaLimit > 0 ? Math.round((totalQuotaUsed / totalQuotaLimit) * 100) : 0;

  const handleDownload = async () => {
    if (data.length === 0) return;
    setDownloading(true);
    try {
      const res = await downloadGpuInfoExcel(data);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'GPU_Info.xlsx';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch (err) {
      toast.error('Download failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="gpu-info-page">
      <div className="page-header">
        <div className="page-header-left">
          <h1>GPU Info</h1>
          <span className="page-subtitle">NVIDIA GPU usage across all projects</span>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary btn-sm" onClick={fetchData} disabled={loading}>
            <HiOutlineRefresh size={14} className={loading ? 'spin' : ''} /> Refresh
          </button>
          <button className="btn btn-secondary btn-sm" onClick={handleDownload} disabled={downloading || data.length === 0}>
            <HiOutlineDownload size={14} /> {downloading ? 'Downloading…' : 'Download Excel'}
          </button>
        </div>
      </div>

      {loading && data.length === 0 ? (
        <div className="gpu-loading">Loading GPU information from all projects…</div>
      ) : (
        <>
          <div className="gpu-summary-cards">
            <div className="gpu-card">
              <div className="gpu-card-icon"><HiOutlineServer size={22} /></div>
              <div className="gpu-card-body">
                <div className="gpu-card-value">{totalGpuVMs}</div>
                <div className="gpu-card-label">GPU VMs</div>
              </div>
            </div>
            <div className="gpu-card">
              <div className="gpu-card-icon"><HiOutlineCloud size={22} /></div>
              <div className="gpu-card-body">
                <div className="gpu-card-value">{totalGpuCR + totalGpuJobs}</div>
                <div className="gpu-card-label">GPU Cloud Run</div>
                <div className="gpu-card-detail">{totalGpuCR} Service{totalGpuCR !== 1 ? 's' : ''} · {totalGpuJobs} Job{totalGpuJobs !== 1 ? 's' : ''}</div>
              </div>
            </div>
            <div className="gpu-card">
              <div className="gpu-card-icon gpu-icon-chip">⬡</div>
              <div className="gpu-card-body">
                <div className="gpu-card-value">{totalGpuCount}</div>
                <div className="gpu-card-label">Total GPUs</div>
              </div>
            </div>
            <div className="gpu-card">
              <div className="gpu-card-icon">%</div>
              <div className="gpu-card-body">
                <div className="gpu-card-value">{overallQuotaPct}%</div>
                <div className="gpu-card-label">Quota Used</div>
              </div>
            </div>
          </div>

          {data.length === 0 && !loading && (
            <p className="gpu-empty">No GPU data found across any project.</p>
          )}

          {data.map((project, i) => (
            <ProjectCard key={project.projectId} project={project} defaultExpanded={i < 3} />
          ))}
        </>
      )}
    </div>
  );
}
